import { BottomSheet, DialogFooter, Illustration, Text } from "@clipboard-health/ui-components";
import { isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { useShowImprovedTierBottomSheet } from "@src/appV2/redesign/Tiers/api/useShowImprovedTierBottomSheet";
import { TIERS_FULL_PATH } from "@src/appV2/redesign/Tiers/paths";
import { TIERS_PERKS_SECTION_ID } from "@src/appV2/redesign/Tiers/utils/constants";
import Confetti from "react-confetti";
import { Link, useHistory } from "react-router-dom";

interface ModalContent {
  Header: () => React.ReactElement;
  Description: () => React.ReactElement;
  buttonText: string;
  illustrationType: "gold-diamond-cloud" | "silver-diamond-cloud";
  learnMorePath: string;
}

// eslint-disable-next-line consistent-return
function getModalContent(newTier: "GOLD" | "SILVER"): ModalContent {
  // eslint-disable-next-line default-case
  switch (newTier) {
    case "GOLD": {
      return {
        Header: () => (
          <Text semibold variant="h4">
            Welcome to{" "}
            <Text semibold inline variant="h4" color={(theme) => theme.palette.tiers?.gold.text}>
              Gold
            </Text>
            !
          </Text>
        ),
        Description: () => (
          <Text variant="body1">
            You earned your place among Clipboard&apos;s top professionals. Enjoy your exclusive
            perks!
          </Text>
        ),
        buttonText: "Check out my perks",
        illustrationType: "gold-diamond-cloud",
        learnMorePath: `${TIERS_FULL_PATH}#${TIERS_PERKS_SECTION_ID}`,
      };
    }

    case "SILVER": {
      return {
        Header: () => (
          <Text semibold variant="h4">
            Welcome to{" "}
            <Text semibold inline variant="h4" color={(theme) => theme.palette.tiers?.silver.text}>
              Silver
            </Text>
            !
          </Text>
        ),
        Description: () => (
          <Text variant="body1">
            Enjoy unlimited bookings! Work your way to{" "}
            <Text inline variant="body1" color={(theme) => theme.palette.tiers?.gold.text}>
              Gold
            </Text>{" "}
            and unlock exclusive perks
          </Text>
        ),
        buttonText: "Learn more",
        illustrationType: "silver-diamond-cloud",
        learnMorePath: TIERS_FULL_PATH,
      };
    }
  }
}

export function ImprovedTierBottomSheet() {
  const { workerTier, improvedTierModalState, workerId } = useShowImprovedTierBottomSheet();
  const logMetadata = { workerId, workerTier };
  const history = useHistory();

  if (isNil(workerTier) || workerTier === "LIMITED") {
    return null;
  }

  const { Header, Description, buttonText, illustrationType, learnMorePath } =
    getModalContent(workerTier);
  return (
    <BottomSheet canDismiss modalState={improvedTierModalState} paperSx={{ overflow: "hidden" }}>
      <Confetti numberOfPieces={500} gravity={0.2} recycle={false} />
      <Stack
        direction="column"
        alignItems="center"
        gap={8}
        sx={{ paddingX: 4, paddingTop: 10, paddingBottom: 8, textAlign: "center" }}
      >
        <Illustration type={illustrationType} />
        <Header />
        <Description />
      </Stack>
      <DialogFooter orientation="vertical" LinkComponent={Link}>
        <Button
          fullWidth
          size="large"
          role="button"
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.IMPROVED_TIER_SEE_MORE_BUTTON_CLICKED, logMetadata);
            improvedTierModalState.closeModal();
            history.push(learnMorePath);
          }}
        >
          {buttonText}
        </Button>
        <Button
          fullWidth
          size="large"
          role="button"
          variant="outlined"
          onClick={async () => {
            logEvent(APP_V2_USER_EVENTS.IMPROVED_TIER_DISMISS_BUTTON_CLICKED, logMetadata);
            improvedTierModalState.closeModal();
          }}
        >
          Got it
        </Button>
      </DialogFooter>
    </BottomSheet>
  );
}
